import React, { useEffect, useMemo, useState } from "react";
import Watermark from "../components/Watermark";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

const ALL_CATEGORIES = [
  { value: "all",         label: "All" },
  { value: "tshirts",     label: "T-shirts" },
  { value: "hoodies",     label: "Hoodies" },
  { value: "bottomwear",  label: "Bottomwear" },
  { value: "accessories", label: "Accessories" },
];
const SIZES = ["XS", "S", "M", "L", "XL"];
const SORTS = [
  { value: "new", label: "New Arrivals" },
  { value: "price-asc", label: "Price ↑" },
  { value: "price-desc", label: "Price ↓" },
];

// Grid columns: mobile always shows 2 (so 4+ products visible), unless user picks 1.
// gridCols controls tablet/desktop max columns. When fewer columns are chosen,
// cards are capped to a sane max-width so object-cover doesn't crop product
// photos into a stretched, wrong-aspect mess.
function gridClass(cols) {
  const base = "grid gap-3 sm:gap-5 lg:gap-7 ";
  if (cols === 1) return base + "grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 [&>*]:max-w-[420px] [&>*]:mx-auto lg:[&>*]:mx-0";
  if (cols === 2) return base + "grid-cols-2 sm:grid-cols-2 lg:grid-cols-2";
  return base + "grid-cols-2 sm:grid-cols-3 lg:grid-cols-3";
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [size, setSize] = useState(null);
  const [sort, setSort] = useState("new");
  const [gridCols, setGridCols] = useState(3);

  // Derive which category filters to show — only categories with real products
  const visibleCategories = useMemo(() => {
    const active = allProducts.filter(p => !p.coming_soon);
    const populated = new Set(active.map(p => p.category));
    return ALL_CATEGORIES.filter(c => c.value === "all" || populated.has(c.value));
  }, [allProducts]);

  useSEO({
    title: "Shop Drop 01 | Million Threads",
    description: "Premium oversized streetwear inspired by anime, manga, and comic book legends. Limited drops, biowashed cotton, laser-etched prints. Shop the first collection.",
  });

  // Load all products once to derive available categories
  useEffect(() => {
    fetchProducts({}).then((d) => setAllProducts(d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchProducts({ category, sort })
      .then((d) => { if (alive) setProducts(d); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, sort]);

  const visible = useMemo(() => {
    if (!size) return products;
    return products.filter((p) => (p.sizes || []).includes(size));
  }, [products, size]);

  return (
    <section className="page-first-section relative overflow-x-hidden" data-testid="shop-section" style={{ paddingBottom: "clamp(2.5rem,6vw,6rem)" }}>
      <Watermark word="THREADS" />
      <div className="mt-container relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10 mt-enter">
          <div>
            <p className="font-mono text-[0.7rem] tracking-mt uppercase flex items-center gap-3" style={{ color: "var(--mt-text-muted)" }}>
              <span className="mt-pulse-dot mt-keep-anim" /> Now Dropping
            </p>
            <h1 className="mt-3 font-display font-extralight leading-[1.08] text-[clamp(2.8rem,7vw,6rem)]">
              The <span className="italic mt-chrome-text mt-keep-anim">Edit.</span>
            </h1>
          </div>
          <p className="font-mono text-[0.7rem] tracking-mt uppercase opacity-60 max-w-xs">
            Built deliberately. Released sparingly. Every drop, a chapter.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-glass mt-rainbow-edge rounded-2xl p-5 sm:p-6 mb-10 mt-enter" data-testid="shop-filters">
          <div className="flex flex-wrap items-center gap-4">
            <FilterGroup label="Category">
              {visibleCategories.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className="mt-pill"
                  data-active={category === c.value}
                  data-testid={`filter-cat-${c.value}`}
                >{c.label}</button>
              ))}
            </FilterGroup>

            <span className="hidden md:inline-block w-px self-stretch" style={{ background: "var(--mt-glass-border)" }} />

            <FilterGroup label="Size">
              <button
                onClick={() => setSize(null)}
                className="mt-pill"
                data-active={size === null}
                data-testid="filter-size-any"
              >Any</button>
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className="mt-pill"
                  data-active={size === s}
                  data-testid={`filter-size-${s}`}
                >{s}</button>
              ))}
            </FilterGroup>

            <span className="hidden md:inline-block w-px self-stretch" style={{ background: "var(--mt-glass-border)" }} />

            <FilterGroup label="Sort">
              {SORTS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSort(s.value)}
                  className="mt-pill"
                  data-active={sort === s.value}
                  data-testid={`filter-sort-${s.value}`}
                >{s.label}</button>
              ))}
            </FilterGroup>

            <span className="hidden md:inline-block w-px self-stretch" style={{ background: "var(--mt-glass-border)" }} />

            <FilterGroup label="View">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  onClick={() => setGridCols(n)}
                  className="mt-pill"
                  data-active={gridCols === n}
                  data-testid={`filter-cols-${n}`}
                  aria-label={`${n} per row`}
                >{n} {n === 1 ? "col" : "cols"}</button>
              ))}
            </FilterGroup>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className={gridClass(gridCols)}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mt-glass rounded-[22px] aspect-[4/5] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className={gridClass(gridCols)} data-testid="shop-grid">
            {visible.map((p, i) => (
              <div key={p.id} className="mt-enter" style={{ transitionDelay: `${(i % 6) * 70}ms` }}>
                <ProductCard product={p} index={i} />
              </div>
            ))}
            {visible.length === 0 && (
              <div className="col-span-full mt-glass rounded-2xl p-16 text-center">
                <p className="font-mono text-[0.7rem] tracking-mt uppercase opacity-70">No pieces match these filters.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
  className="font-mono text-[0.6rem] tracking-mt uppercase mr-2"
  style={{ color: "var(--mt-text)" }}
>
  {label}
</span>
      {children}
    </div>
  );
}
